/**
 * @license
 * Copyright 2026 Aglyn LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Is this session still able to read from the server? (AGL-1063)
 *
 * A module-level store rather than context, because the thing that knows —
 * `firestore-one-shot-retry` — is a plain async helper called from effects,
 * not a component. Subscribers (the shell banner) get the derived verdict.
 *
 * ## The distinction this exists to draw
 *
 * `permission-denied` is treated as transient everywhere, and correctly so:
 * AGL-216/217 established a real race where `useUser()` reports a signed-in
 * user a beat before Firestore attaches the ID token. A denial that will
 * NEVER clear looks identical from the inside. AGL-1062 was that case — a
 * stale session denied every server read for hours while
 * `persistentLocalCache` kept listeners rendering, and nothing anywhere said
 * so.
 *
 * ## Why one denial is not enough
 *
 * Surviving all five retries already means "not the sign-in race". It does
 * NOT mean "session". A scoped collaborator reading something AGL-1041
 * deliberately hides gets exactly this shape, forever, by design — and
 * prompting them to re-authenticate mid-edit would be worse than the silent
 * degradation this replaces. What a scoped collaborator does NOT produce is
 * denials across unrelated collections: their `visibleTo` filter is per
 * collection, and the collections they may read keep working. A session that
 * is dead denies everything.
 *
 * So the verdict needs {@link SESSION_STALE_MIN_COLLECTIONS} DISTINCT
 * collections denied inside {@link SESSION_STALE_WINDOW_MS}. Unlabelled
 * reports all share one bucket, so a caller that forgets to name its
 * collection can only ever fail to trigger the prompt — never cause one.
 */

/**
 * Distinct collections that must exhaust their retries before the console
 * says the session is the problem. Two is the smallest number that cannot
 * be produced by a single deliberately-hidden collection (AGL-1041).
 *
 * Reviewed and kept at 2 on 2026-07-28. Raising it to 3 buys margin against
 * a page that legitimately reads two scoped collections back to back, but
 * costs nothing only for sessions that are genuinely dead — those deny
 * every collection and reach any threshold at the same speed. What it does
 * cost is real: a page issuing only two distinct reads could then never
 * prompt at all. Two plus {@link reportSuccessfulRead} clearing the
 * evidence outright is a tight enough gate — a false prompt needs two
 * unrelated scoped denials with no successful server read between them.
 */
export const SESSION_STALE_MIN_COLLECTIONS = 2

/**
 * How long a denial counts toward the verdict. Long enough to span a page
 * whose reads are staggered, short enough that two unrelated genuine
 * denials minutes apart never add up to a false prompt.
 *
 * Reviewed and kept at 60s on 2026-07-28. Widening it to 300s would catch
 * sessions whose reads straggle across a slow page or a navigation, at the
 * price of letting two genuine scoped denials five minutes apart accumulate
 * into a prompt. A false prompt that interrupts an edit is the outcome this
 * whole mechanism is tuned against, so the shorter window wins.
 */
export const SESSION_STALE_WINDOW_MS = 60_000

export interface SessionHealthState {
  /** True once the evidence says the SESSION, not the authorization. */
  staleSession: boolean
  /** Collection keys that exhausted their retries, most recent last. */
  deniedCollections: string[]
  /**
   * How many reads have reached the SERVER since this page loaded.
   *
   * The episode counter (AGL-2486). A stale verdict on its own cannot tell
   * "still the same dead session the user was already asked about" from "a
   * fresh failure": {@link prune} drops evidence after
   * {@link SESSION_STALE_WINDOW_MS} of quiet, so `staleSession` flips back to
   * false on an idle tab and true again on the next two denials — with
   * nothing having changed. Anything that must act ONCE per episode (the
   * automatic re-auth prompt) latches on this number instead, because it
   * moves only when a read actually reaches the server, which is the one
   * event that ends an episode.
   */
  serverReads: number
}

/** Collection key → when it last exhausted its retries. */
const denials = new Map<string, number>()
/** Monotonic; see {@link SessionHealthState.serverReads}. */
let serverReads = 0
const listeners = new Set<(state: SessionHealthState) => void>()

/** Injectable clock so tests do not sleep. */
let now = () => Date.now()

function prune() {
  const cutoff = now() - SESSION_STALE_WINDOW_MS
  for (const [key, at] of denials) if (at < cutoff) denials.delete(key)
}

export function getSessionHealth(): SessionHealthState {
  prune()
  const deniedCollections = [...denials.entries()]
    .sort((a, b) => a[1] - b[1])
    .map(([key]) => key)
  return {
    staleSession: deniedCollections.length >= SESSION_STALE_MIN_COLLECTIONS,
    deniedCollections,
    serverReads,
  }
}

function publish() {
  const state = getSessionHealth()
  for (const listener of listeners) listener(state)
}

/**
 * A one-shot read exhausted its retries on `permission-denied`.
 *
 * `collection` is the evidence, not a label: two reports of the same key are
 * one collection and never reach the threshold on their own.
 */
export function reportDeniedRead(collection = 'unknown'): void {
  denials.set(collection, now())
  publish()
}

/**
 * A read reached the SERVER and succeeded.
 *
 * That is proof the session is alive — stronger than any accumulated
 * denial — so it clears the evidence outright. Without this, two genuine
 * denials early in a session would leave the prompt armed for the rest of
 * it.
 *
 * Also called by the listener hooks for any snapshot with
 * `metadata.fromCache === false` (AGL-1066), which is what makes listener
 * denials safe to count: a scoped collaborator's other listens keep being
 * answered and clear the evidence, while a dead session has no answer to
 * offer.
 *
 * NOTE: one-shot reads do NOT unconditionally bypass the cache, as this
 * comment used to claim. `getDocs` with the default source falls back to
 * the cache when the client is OFFLINE, and succeeds — so this can be
 * reached without the server having answered. Harmless in practice, because
 * an offline client accumulates no `permission-denied` evidence for it to
 * clear, but do not build anything on the stronger reading. A refusal is
 * still always the server's, which is the part that matters.
 */
export function reportSuccessfulRead(): void {
  // Counted BEFORE the early return, and unconditionally: this number exists
  // to date an episode, and an episode can end with the evidence already
  // pruned away. Bumping it only when there was evidence left to clear would
  // leave a latch armed against a recovery that really happened.
  serverReads++
  if (!denials.size) return
  denials.clear()
  publish()
}

export function subscribeSessionHealth(
  listener: (state: SessionHealthState) => void,
): () => void {
  listeners.add(listener)
  listener(getSessionHealth())
  return () => void listeners.delete(listener)
}

/** Test seam: reset the store and (optionally) pin the clock. */
export function __resetSessionHealth(clock?: () => number): void {
  denials.clear()
  serverReads = 0
  now = clock ?? (() => Date.now())
}

export default getSessionHealth
